import { router, Stack, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { EventForm, type EventEditorCommunity } from '@/components/events/event-form';
import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';
import { Button } from '@sixam/ui/button';
import { Text } from '@sixam/ui/text';
import { isEventEditable } from '@sixam/shared/event-editing-core';
import { fetchEventById, type Event } from '@/lib/events';
import { adminTenantsForUser } from '@/lib/tenant';
import { useCurrentTime } from '@/lib/dev-clock';

export default function AdminGlobalEditEventScreen() {
  const now = useCurrentTime();
  const { id, routeVersionId } = useLocalSearchParams<{ id: string; routeVersionId?: string }>();
  const { isAdmin, isProfileLoading } = useSession();
  const { isTenantLoading, memberships, tenants } = useTenant();
  const [event, setEvent] = React.useState<Event | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const communities = React.useMemo<EventEditorCommunity[]>(
    () =>
      adminTenantsForUser(tenants, memberships, isAdmin).map(({ id, name, slug }) => ({
        id,
        name,
        slug,
      })),
    [isAdmin, memberships, tenants]
  );
  const eventCommunity = communities.find((community) => community.id === event?.tenant_id) ?? null;

  const returnToHome = () => {
    router.replace('/app/home');
  };

  const openSavedEvent = (savedEvent: Event) => {
    router.replace({
      pathname: '/app/events/[id]',
      params: { id: savedEvent.id },
    });
  };

  React.useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      try {
        const nextEvent = await fetchEventById(id);
        if (!active) return;
        if (!nextEvent) throw new Error('Event not found.');
        setEvent(nextEvent);
        setError(null);
      } catch (loadError) {
        if (active) {
          setError(loadError instanceof Error ? loadError.message : 'Could not load event.');
        }
      } finally {
        if (active) setIsLoading(false);
      }
    }
    void load();
    return () => {
      active = false;
    };
  }, [id]);

  if (isLoading || isProfileLoading || isTenantLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <Stack.Screen options={{ headerShown: false }} />
        <ActivityIndicator />
      </View>
    );
  }

  if (error || !event || !eventCommunity) {
    return (
      <View className="flex-1 items-center justify-center gap-3 bg-background p-6">
        <Stack.Screen options={{ headerShown: false }} />
        <Text className="text-center text-destructive">
          {error ?? 'You do not have permission to edit this event.'}
        </Text>
      </View>
    );
  }

  if (!isEventEditable(event, now)) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-background p-6">
        <Stack.Screen options={{ headerShown: false }} />
        <Text variant="h3" className="text-center">
          Past event
        </Text>
        <Text variant="muted" className="text-center">
          Events cannot be edited after they end.
        </Text>
        <Button onPress={returnToHome}>
          <Text>Back to home</Text>
        </Button>
      </View>
    );
  }

  return (
    <EventForm
      event={event}
      initialRouteVersionId={routeVersionId}
      initialSheet={routeVersionId ? 'route' : null}
      onClose={returnToHome}
      onSaved={openSavedEvent}
      tenantId={eventCommunity.id}
      tenantName={eventCommunity.name}
      tenantSlug={eventCommunity.slug}
    />
  );
}
